import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 ERRO CAPTURADO PELO ERROR BOUNDARY:', error);
    console.error('📊 INFO DO ERRO:', errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log do erro para análise
    console.group('🔍 DETALHES COMPLETOS DO ERRO');
    console.error('Mensagem:', error.message);
    console.error('Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Timestamp:', new Date().toISOString());
    console.error('URL:', window.location.href);
    console.error('User Agent:', navigator.userAgent);
    console.groupEnd();
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg border border-red-200">
            {/* Header */}
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-red-900">
                    Ops! Algo deu errado
                  </h1>
                  <p className="text-red-700">
                    Ocorreu um erro inesperado no sistema
                  </p>
                </div>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="px-6 py-6">
              <div className="text-center mb-6">
                <p className="text-gray-600 mb-4">
                  Desculpe pelo inconveniente. O sistema encontrou um erro inesperado.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-700 font-medium mb-2">
                    O que você pode fazer:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 text-left">
                    <li>• Recarregar a página e tentar novamente</li>
                    <li>• Verificar se todos os campos estão preenchidos</li>
                    <li>• Tentar em alguns minutos</li>
                    <li>• Contatar o suporte se o problema persistir</li>
                  </ul>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Recarregar Página</span>
                </button>
                
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  <span>Ir para o Início</span>
                </button>
              </div>

              {/* Detalhes Técnicos (Opcional) */}
              {this.state.error && (
                <div className="mt-6">
                  <button
                    onClick={this.toggleDetails}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    {this.state.showDetails ? 'Ocultar' : 'Mostrar'} detalhes técnicos
                  </button>
                  
                  {this.state.showDetails && (
                    <div className="mt-3 bg-gray-100 rounded-lg p-4 text-left">
                      <p className="text-xs font-mono text-gray-800 break-all">
                        <strong>Erro:</strong> {this.state.error.toString()}
                      </p>
                      {this.state.errorInfo && this.state.errorInfo.componentStack && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-600 cursor-pointer">
                            Stack do Componente
                          </summary>
                          <pre className="text-xs text-gray-800 mt-2 whitespace-pre-wrap">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 rounded-b-lg">
              <p className="text-xs text-gray-500 text-center">
                Se o problema persistir, contate o suporte técnico
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
